/** The Onboarding page's server state: ONE poll of `GET /v2/edit/onboarding/state/`, modelled on the
 *  console's useEasyOnboarding.ts but without react-query (the dashboard has none).
 *
 *  - every `pollMs` (3 s) while something moves on its own (`isActive`: discovery running, a lane
 *    running, a chosen import still bound to run, going live);
 *  - stopped once nothing will change without the client (`isSettled`: Ready — or an import that
 *    failed — with no go-live request waiting on our team);
 *  - otherwise every `slowMs` (30 s): the contract's "ops may act meanwhile" (a go-live request
 *    waiting for a decision, the tour choice open), and while the sandbox asked for at sign-up is set
 *    up from Ready on (`isSandboxBusy`; the contract: "every 30 s is enough");
 *  - a user action (`afterAction`) reads at once and polls every `pollMs` for `resumeMs` (30 s), since
 *    it may start work the state does not show yet;
 *  - a 4xx stops the poll (no permission, not an easy tenant: a retry will not change it); a 5xx or a
 *    network error (a deploy, a blip) backs off — `backoffMs`, doubling per failed read up to
 *    `maxBackoffMs` — keeps the last good state on screen, and `reconnecting` says so;
 *  - nothing is read while the tab is hidden; it reads at once when the tab is shown again. */
import * as React from 'react';

import { usePageVisibility } from '../components/usePageVisibility';
import { isClientError } from './api';
import type { OnboardingState } from './types';

export const POLL_MS = 3000;
export const SLOW_MS = 30_000;
export const RESUME_MS = 30_000;
export const BACKOFF_MS = 2000;
export const MAX_BACKOFF_MS = 30_000;

/** The sandbox asked for at sign-up is being set up (from Ready on: before it, "requested" only waits). */
export function isSandboxBusy(s: OnboardingState | null | undefined): boolean {
    const status = s?.sandbox?.status;
    if (!status) {
        return false;
    }
    return status === 'creating' || status === 'syncing' || (status === 'requested' && s!.phase === 'ready');
}

/** Something moves on its own now: poll fast. */
export function isActive(s: OnboardingState | null | undefined): boolean {
    if (!s) {
        return false;
    }
    return (
        s.discovery.running
        || s.lanes.some((l) => l.state === 'running')
        || (s.phase === 'importing' && s.selection.locked)
        || s.go_live.status === 'running'
        || s.go_live.status === 'approved'
    );
}

/** A go-live request waiting for our team: nothing moves on its own, but ops may decide any time. */
export function isWaitingOnTeam(s: OnboardingState | null | undefined): boolean {
    return !!s && s.go_live.status === 'requested';
}

/** An import that cannot move on: still `importing`, no import bound to run, and nothing running. */
export function isStalled(s: OnboardingState | null | undefined): boolean {
    return !!s && s.phase === 'importing' && !s.selection.locked && !isActive(s);
}

/** Nothing will change without the client: stop polling. */
export function isSettled(s: OnboardingState | null | undefined): boolean {
    if (!s || isActive(s) || isWaitingOnTeam(s) || isSandboxBusy(s)) {
        return false;
    }
    return s.go_live.status === 'live' || s.phase === 'ready' || isStalled(s);
}

/** The wait before the next read after `failures` failed reads in a row (1 → `base`). */
export function backoffDelay(failures: number, base = BACKOFF_MS, max = MAX_BACKOFF_MS): number {
    return Math.min(max, base * 2 ** Math.max(0, failures - 1));
}

/** The wait before the next read after a good one, or null to stop. */
export function nextDelay(
    s: OnboardingState | null | undefined,
    { resumed = false, pollMs = POLL_MS, slowMs = SLOW_MS }: { resumed?: boolean; pollMs?: number; slowMs?: number } = {},
): number | null {
    if (resumed || isActive(s)) {
        return pollMs;
    }
    if (isSettled(s)) {
        return null;
    }
    return slowMs;
}

interface StateApi {
    state: (signal?: AbortSignal) => Promise<OnboardingState>;
}

interface Options {
    enabled?: boolean;
    pollMs?: number;
    slowMs?: number;
    resumeMs?: number;
    backoffMs?: number;
    maxBackoffMs?: number;
    /** Every good read (the session's onboarding status follows the page). */
    onState?: (s: OnboardingState) => void;
    /** Every failed read. */
    onError?: (e: unknown) => void;
}

export interface OnboardingStateResult {
    state: OnboardingState | null;
    /** The last failed read (cleared by a good one). */
    error: unknown;
    /** No state yet and the first read is under way. */
    loading: boolean;
    /** A transient failure the poll is riding out with the last good state on screen. */
    reconnecting: boolean;
    /** Read now and keep polling fast for a while: call after an action that may start work. */
    afterAction: () => void;
    /** Read now. */
    refresh: () => void;
}

export function useOnboardingState(api: StateApi, opts: Options = {}): OnboardingStateResult {
    const {
        enabled = true,
        pollMs = POLL_MS,
        slowMs = SLOW_MS,
        resumeMs = RESUME_MS,
        backoffMs = BACKOFF_MS,
        maxBackoffMs = MAX_BACKOFF_MS,
    } = opts;
    const visible = usePageVisibility();
    const [state, setState] = React.useState<OnboardingState | null>(null);
    const [error, setError] = React.useState<unknown>(null);
    const [loading, setLoading] = React.useState(enabled);

    const onStateRef = React.useRef(opts.onState);
    onStateRef.current = opts.onState;
    const onErrorRef = React.useRef(opts.onError);
    onErrorRef.current = opts.onError;
    const timer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    const inflight = React.useRef<AbortController | null>(null);
    const failures = React.useRef(0);
    const resumedAt = React.useRef(0);
    const live = React.useRef({ enabled, visible });
    live.current = { enabled, visible };
    const settings = React.useRef({ pollMs, slowMs, resumeMs, backoffMs, maxBackoffMs });
    settings.current = { pollMs, slowMs, resumeMs, backoffMs, maxBackoffMs };

    const clearTimer = () => {
        if (timer.current) {
            clearTimeout(timer.current);
            timer.current = null;
        }
    };

    const read = React.useCallback(() => {
        clearTimer();
        inflight.current?.abort();
        if (!live.current.enabled || !live.current.visible) {
            return;
        }
        const ctrl = new AbortController();
        inflight.current = ctrl;
        const schedule = (delay: number | null) => {
            if (delay != null && live.current.enabled && live.current.visible) {
                timer.current = setTimeout(read, delay);
            }
        };
        api.state(ctrl.signal).then(
            (s) => {
                if (ctrl.signal.aborted) {
                    return;
                }
                inflight.current = null;
                failures.current = 0;
                setState(s);
                setError(null);
                setLoading(false);
                onStateRef.current?.(s);
                const cfg = settings.current;
                const resumed = Date.now() - resumedAt.current < cfg.resumeMs;
                schedule(nextDelay(s, { resumed, pollMs: cfg.pollMs, slowMs: cfg.slowMs }));
            },
            (e) => {
                if (ctrl.signal.aborted || (e && e.name === 'AbortError')) {
                    return;
                }
                inflight.current = null;
                setError(e);
                setLoading(false);
                onErrorRef.current?.(e);
                if (isClientError(e)) {
                    return;
                }
                failures.current += 1;
                const cfg = settings.current;
                schedule(backoffDelay(failures.current, cfg.backoffMs, cfg.maxBackoffMs));
            },
        );
    }, [api]);

    // Start, and restart when the tab is shown again; stop when it is hidden or disabled.
    React.useEffect(() => {
        if (enabled && visible) {
            read();
        }
        return () => {
            clearTimer();
            inflight.current?.abort();
            inflight.current = null;
        };
    }, [enabled, visible, read]);

    const afterAction = React.useCallback(() => {
        resumedAt.current = Date.now();
        read();
    }, [read]);

    const reconnecting = error != null && !isClientError(error) && state != null;
    return { state, error, loading: loading && state == null, reconnecting, afterAction, refresh: read };
}
